'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { smoothScrollToElement } from 'lib/hooks/useScrollElementIntoView';

const HASH_SCROLL_MAX_ATTEMPTS = 20;
const HASH_SCROLL_INTERVAL_MS = 50;

function getHashTargetId(): string | null {
  const hash = window.location.hash;
  if (!hash || hash === '#') {
    return null;
  }
  return decodeURIComponent(hash.slice(1));
}

function scrollToTop() {
  window.scrollTo(0, 0);
}

function scrollToHashTarget(): boolean {
  const id = getHashTargetId();
  if (!id) {
    return false;
  }

  const element = document.getElementById(id);
  if (!element) {
    return false;
  }

  smoothScrollToElement(element, 'header');
  return true;
}

function runHashScrollWithRetry(onGiveUp?: () => void) {
  let attempts = 0;
  const intervalId = window.setInterval(() => {
    attempts += 1;
    if (scrollToHashTarget()) {
      window.clearInterval(intervalId);
      return;
    }
    if (attempts >= HASH_SCROLL_MAX_ATTEMPTS) {
      window.clearInterval(intervalId);
      onGiveUp?.();
    }
  }, HASH_SCROLL_INTERVAL_MS);

  return () => window.clearInterval(intervalId);
}

/**
 * Global scroll restoration for App Router navigations.
 *
 * Mounted in the root layout so it survives Sitecore page remounts. Sitecore
 * pages re-create `Layout` on every navigation, which breaks scroll handling
 * inside that tree.
 *
 * - No hash: scroll to top on pathname change.
 * - With hash: scroll to the target section (retries until content hydrates).
 */
export default function ScrollToTopOnNavigate() {
  const pathname = usePathname();

  useEffect(() => {
    if (getHashTargetId()) {
      return runHashScrollWithRetry(scrollToTop);
    }

    scrollToTop();
    const frameId = requestAnimationFrame(scrollToTop);
    return () => cancelAnimationFrame(frameId);
  }, [pathname]);

  useEffect(() => {
    const onHashChange = () => {
      if (getHashTargetId()) {
        runHashScrollWithRetry();
      }
    };

    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  return null;
}
